import { useState, useMemo } from 'react';
import { StatusBadge } from '@/components/base/StatCard';
import { services, serviceCategories, type Service, type ServiceCategory } from '@/mocks/services';

const allStatuses = ['Tümü', 'Aktif', 'Pasif'];

interface ServiceModalProps {
  service: Service | null;
  onClose: () => void;
  onSave: (s: Service) => void;
}

function ServiceModal({ service, onClose, onSave }: ServiceModalProps) {
  const [form, setForm] = useState<Partial<Service>>(service || {
    id: `srv-${Date.now()}`,
    name: '',
    category: 'Masaj',
    duration: 60,
    price: 0,
    commission: 0,
    active: true,
    description: '',
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={onClose} />
      <div className="relative bg-theme-card rounded-spa w-full max-w-lg flex flex-col max-h-[90vh] animate-scale-in border" style={{ borderColor: 'var(--card-border)' }}>
        <div className="sticky top-0 flex items-center justify-between p-4 border-b z-10 flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
            {service ? 'Hizmet Düzenle' : 'Yeni Hizmet'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Hizmet Adı</label>
            <input className="spa-input" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Hizmet adı" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Kategori</label>
              <select className="spa-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value as ServiceCategory })}>
                {serviceCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Süre (dk)</label>
              <input type="number" className="spa-input" value={form.duration} onChange={e => setForm({ ...form, duration: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Satış Fiyatı (₺)</label>
              <input type="number" className="spa-input" value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Personel Prim (₺)</label>
              <input type="number" className="spa-input" value={form.commission} onChange={e => setForm({ ...form, commission: Number(e.target.value) })} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="svcActive" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[var(--gold)]" />
            <label htmlFor="svcActive" className="text-sm" style={{ color: 'var(--text-primary)' }}>Aktif</label>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Açıklama</label>
            <textarea className="spa-input min-h-[80px] resize-none" maxLength={500} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Hizmet açıklaması..." />
          </div>
        </div>
        <div className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <button onClick={onClose} className="spa-btn-secondary text-sm">İptal</button>
          <button onClick={() => { onSave(form as Service); onClose(); }} className="spa-btn-primary text-sm">{service ? 'Güncelle' : 'Oluştur'}</button>
        </div>
      </div>
    </div>
  );
}

export default function HizmetlerPage() {
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('Tümü');
  const [statusFilter, setStatusFilter] = useState<string>('Tümü');
  const [serviceList, setServiceList] = useState<Service[]>(services);
  const [modalOpen, setModalOpen] = useState(false);
  const [editService, setEditService] = useState<Service | null>(null);

  const filtered = useMemo(() => {
    let data = [...serviceList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'Tümü') data = data.filter(s => s.category === categoryFilter);
    if (statusFilter !== 'Tümü') data = data.filter(s => statusFilter === 'Aktif' ? s.active : !s.active);
    return data;
  }, [serviceList, searchQuery, categoryFilter, statusFilter]);

  const handleSave = (svc: Service) => {
    if (editService) {
      setServiceList(prev => prev.map(s => s.id === svc.id ? svc : s));
    } else {
      setServiceList(prev => [{ ...svc, id: `srv-${Date.now()}` }, ...prev]);
    }
    setEditService(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Bu hizmeti silmek istediğinize emin misiniz?')) {
      setServiceList(prev => prev.filter(s => s.id !== id));
    }
  };

  const stats = useMemo(() => ({
    total: serviceList.length,
    active: serviceList.filter(s => s.active).length,
    passive: serviceList.filter(s => !s.active).length,
    avgPrice: Math.round(serviceList.reduce((sum, s) => sum + s.price, 0) / (serviceList.length || 1)),
  }), [serviceList]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Hizmetler</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} hizmet kaydı</p>
        </div>
        <button onClick={() => { setEditService(null); setModalOpen(true); }} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Hizmet
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: stats.total, icon: 'ri-drop-line', color: '#C9A962' },
          { label: 'Aktif', value: stats.active, icon: 'ri-check-line', color: '#10B981' },
          { label: 'Pasif', value: stats.passive, icon: 'ri-close-line', color: '#EF4444' },
          { label: 'Ort. Fiyat', value: `₺${stats.avgPrice}`, icon: 'ri-coins-line', color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Hizmet adı veya açıklama ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[120px]" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="Tümü">Tüm Kategoriler</option>
            {serviceCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex rounded-spa-sm overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
            <button onClick={() => setViewMode('list')} className="px-3 py-2 text-sm cursor-pointer" style={{ background: viewMode === 'list' ? 'var(--gold)' : 'var(--bg-tertiary)', color: viewMode === 'list' ? '#FFFFFF' : 'var(--text-secondary)' }}>
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check-2" /></div>
            </button>
            <button onClick={() => setViewMode('card')} className="px-3 py-2 text-sm cursor-pointer" style={{ background: viewMode === 'card' ? 'var(--gold)' : 'var(--bg-tertiary)', color: viewMode === 'card' ? '#FFFFFF' : 'var(--text-secondary)' }}>
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-grid-line" /></div>
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="spa-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                  {['Hizmet', 'Kategori', 'Süre', 'Fiyat', 'Prim', 'Durum', 'İşlemler'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(svc => (
                  <tr key={svc.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="px-3 py-3">
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{svc.name}</p>
                      <p className="text-xs truncate max-w-[200px]" style={{ color: 'var(--text-muted)' }}>{svc.description}</p>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{svc.category}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{svc.duration} dk</td>
                    <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: 'var(--text-primary)' }}>₺{svc.price.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--gold)' }}>₺{svc.commission}</td>
                    <td className="px-3 py-3"><StatusBadge status={svc.active ? 'Aktif' : 'Pasif'}>{svc.active ? 'Aktif' : 'Pasif'}</StatusBadge></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditService(svc); setModalOpen(true); }} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'var(--bg-tertiary)' }} title="Düzenle"><i className="ri-edit-line text-xs" style={{ color: 'var(--text-secondary)' }} /></button>
                        <button onClick={() => handleDelete(svc.id)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil"><i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(svc => (
            <div key={svc.id} className="spa-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-spa-sm flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-tertiary)' }}>
                    <i className="ri-drop-line text-sm" style={{ color: 'var(--gold)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{svc.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{svc.category}</p>
                  </div>
                </div>
                <StatusBadge status={svc.active ? 'Aktif' : 'Pasif'}>{svc.active ? 'Aktif' : 'Pasif'}</StatusBadge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{svc.duration}dk</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Süre</p>
                </div>
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>₺{svc.price}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Fiyat</p>
                </div>
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: 'var(--gold)' }}>₺{svc.commission}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Prim</p>
                </div>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{svc.description}</p>
              <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <button onClick={() => { setEditService(svc); setModalOpen(true); }} className="flex-1 spa-btn-secondary text-xs py-2">Düzenle</button>
                <button onClick={() => handleDelete(svc.id)} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>Sil</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-drop-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Hizmet bulunamadı</p>
        </div>
      )}

      {modalOpen && <ServiceModal service={editService} onClose={() => setModalOpen(false)} onSave={handleSave} />}
    </div>
  );
}